<script setup>
// Picks the colleague a live call is handed over to. The backend only returns
// people the PBX can actually reach right now — mapped to an employee and in a
// status that still takes calls — so anything shown here is safe to pick.
import { ref, computed, onMounted, nextTick } from 'vue';
import MangoAPI from 'dashboard/api/mango';

const props = defineProps({
  inboxId: { type: [Number, String], required: true },
});
const emit = defineEmits(['select', 'close']);

const targets = ref([]);
const query = ref('');
const isLoading = ref(true);
const hasFailed = ref(false);
const searchInput = ref(null);

const results = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return targets.value;
  return targets.value.filter(target =>
    [target.name, target.position, target.extension]
      .filter(Boolean)
      .some(field => String(field).toLowerCase().includes(q))
  );
});

onMounted(async () => {
  try {
    const { data } = await MangoAPI.transferTargets(props.inboxId);
    targets.value = data.targets || [];
  } catch (error) {
    hasFailed.value = true;
  } finally {
    isLoading.value = false;
    // The operator is mid-call: let them type straight away instead of aiming
    // at the field with a mouse.
    await nextTick();
    searchInput.value?.focus();
  }
});
</script>

<template>
  <div
    class="flex flex-col gap-2 p-2 rounded-xl bg-n-alpha-1 border border-n-strong"
  >
    <div class="flex items-center gap-2">
      <input
        ref="searchInput"
        v-model="query"
        type="text"
        class="flex-1 mb-0"
        :placeholder="$t('CONVERSATION.VOICE_CALL.TRANSFER_SEARCH')"
        @keyup.esc="emit('close')"
      />
      <button
        type="button"
        class="p-1 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
        :aria-label="$t('CONVERSATION.VOICE_CALL.TRANSFER_CANCEL')"
        @click="emit('close')"
      >
        <span class="i-lucide-x size-4" />
      </button>
    </div>

    <p v-if="isLoading" class="px-1 py-2 text-sm text-n-slate-11">
      {{ $t('CONVERSATION.VOICE_CALL.TRANSFER_LOADING') }}
    </p>
    <p v-else-if="hasFailed" class="px-1 py-2 text-sm text-n-ruby-11">
      {{ $t('CONVERSATION.VOICE_CALL.TRANSFER_LOAD_FAILED') }}
    </p>
    <p v-else-if="!targets.length" class="px-1 py-2 text-sm text-n-slate-11">
      {{ $t('CONVERSATION.VOICE_CALL.TRANSFER_NOBODY') }}
    </p>
    <p v-else-if="!results.length" class="px-1 py-2 text-sm text-n-slate-11">
      {{ $t('CONVERSATION.VOICE_CALL.TRANSFER_NO_MATCH') }}
    </p>

    <ul v-else class="flex flex-col gap-0.5 max-h-56 overflow-y-auto">
      <li v-for="target in results" :key="target.user_id">
        <button
          type="button"
          class="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-n-alpha-2"
          @click="emit('select', target)"
        >
          <span class="flex-1 min-w-0">
            <span class="block text-sm text-n-slate-12 truncate">
              {{ target.name }}
            </span>
            <span
              v-if="target.position"
              class="block text-xs text-n-slate-11 truncate"
            >
              {{ target.position }}
            </span>
          </span>
          <span class="text-xs text-n-slate-11 tabular-nums shrink-0">
            {{ target.extension }}
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>
